import { BrowserStorageType, ButtonTypeEnum, ReCaptchaStatusEnum, SignUpModeEnum } from '@app/app.enum';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import PasswordTextFieldComponent from '@app/components/shared/password-text-field.component';
import { ChangePasswordRegex, CompanyNameMaxLength, CompanyNameMinLength, CompanyNameRegex, EmailMaxLength, FirstNameMaxLength, LastNameMaxLength } from '@app/constants';
import environment from '@app/environments/environment';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { Box, Checkbox, FormControlLabel, Link, TextField } from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React, { Component } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);

const SignupSchemaShape: Yup.ObjectSchemaDefinition<models.ISignUpFormFields> = {
	companyName: Yup.string()
		.trim()
		.required(intlService.GetFormattedMessage('app.signup.signup-form.company-name-required-validation'))
		.min(CompanyNameMinLength, intlService.GetFormattedMessage('app.signup.signup-form.company-min-length-required-validation'))
		.max(CompanyNameMaxLength, intlService.GetFormattedMessage('app.signup.signup-form.company-max-length-required-validation'))
		.matches(CompanyNameRegex, intlService.GetFormattedMessage('app.signup.company-name-regex-validation')),
	firstName: Yup.string()
		.trim()
		.max(FirstNameMaxLength, intlService.GetFormattedMessage('app.signup.signup-form.first-name-length-validation', { 0: FirstNameMaxLength }))
		.required(intlService.GetFormattedMessage('app.signup.signup-form.first-name-required-validation')),
	lastName: Yup.string()
		.trim()
		.max(LastNameMaxLength, intlService.GetFormattedMessage('app.signup.signup-form.last-name-length-validation', { 0: LastNameMaxLength }))
		.required(intlService.GetFormattedMessage('app.signup.signup-form.last-name-required-validation')),
	email: Yup.string()
		.trim()
		.max(EmailMaxLength, intlService.GetFormattedMessage('app.signup.signup-form.email-length-validation', { 0: EmailMaxLength }) )
		.email(intlService.GetFormattedMessage('app.signup.signup-form.email-invalid-validation'))
		.required(intlService.GetFormattedMessage('app.signup.signup-form.email-required-validation')),
	password: Yup.string()
		.trim()
		.required(intlService.GetFormattedMessage('app.signup.signup-form.password-required-validation'))
		.matches(ChangePasswordRegex, intlService.GetFormattedMessage('app.profile.change-password-form.regex-validation')),
	reCaptchaResponse: Yup.string().notRequired(),
};

class SignUpFormComponent extends Component<models.ISignUpFormComponentProps, models.ISignUpFormComponentState> {
	public static defaultProps: Partial<models.ISignUpFormComponentProps> = {
		quickSignUpConfiguration: {
			disableForm: false,
			prefillFormValues: null,
			disabledFormAction: () => {},
		},
	};
	private isQuickSignUp: boolean;
	private signupSchema: Yup.ObjectSchema<Yup.Shape<object, models.ISignUpFormFields>> =
		Yup.object().shape<models.ISignUpFormFields>(SignupSchemaShape);

	private recaptchaRef = React.createRef<ReCAPTCHA>();

	private FormFactory: models.ISignUpFormFields = {
		companyName: '',
		firstName: '',
		lastName: '',
		email: '',
		password: '',
		signupKey: '',
		reCaptchaResponse: '',
	};

	constructor(props: models.ISignUpFormComponentProps) {
		super(props);
		this.state = {
			showAPIKeyTextBox: false,
			isSignUpKeyInQueryParam: !!props.signUpKey,
			isTermsAccepted: false,
			loading: false,
		};
		this.isQuickSignUp = props.mode === SignUpModeEnum.Quick;
	}

	public componentDidUpdate(prevProps: models.ISignUpFormComponentProps, prevState: models.ISignUpFormComponentState) {
		if (prevProps.signUpKey !== this.props.signUpKey) {
			this.setState({
				isSignUpKeyInQueryParam: !!this.props.signUpKey,
			});
		}
	}

	public render() {
		// Update sign up form data if any prefill data provided by parent
		this.updateSignUpFormData();

		const { disableForm } = this.props.quickSignUpConfiguration;

		return (
			<Formik
				initialValues={this.FormFactory}
				onSubmit={this.onUserSignedUp}
				validationSchema={this.signupSchema}
				enableReinitialize={true}
				render={(props: FormikProps<FormikValues>) => (
					<Form noValidate={true}>
						<Field
							name='companyName'
							inputProps={{
								'data-testid': 'companyName',
							}}
							label={this.props.intl.formatMessage({
								id: 'app.signup.company-name-label',
							})}
							disabled={disableForm}
							component={TextField}
							type='text'
							value={props.values.companyName}
							variant='outlined'
							fullWidth={true}
							required={true}
							margin='normal'
							onChange={(e: React.ChangeEvent) => props.handleChange('companyName')(e)}
							error={!!((props.values.companyName.length || props.submitCount) && !!props.errors.companyName)}
							helperText={props.values.companyName.length || props.submitCount ? props.errors.companyName : null}
						/>
						<Field
							name='firstName'
							inputProps={{
								'data-testid': 'firstName',
							}}
							label={this.props.intl.formatMessage({
								id: 'app.signup.first-name-label',
							})}
							disabled={disableForm}
							component={TextField}
							type='text'
							value={props.values.firstName}
							variant='outlined'
							fullWidth={true}
							required={true}
							margin='normal'
							onChange={(e: React.ChangeEvent) => props.handleChange('firstName')(e)}
							error={!!((props.values.firstName.length || props.submitCount) && !!props.errors.firstName)}
							helperText={props.values.firstName.length || props.submitCount ? props.errors.firstName : null}
						/>
						<Field
							name='lastName'
							inputProps={{
								'data-testid': 'lastName',
							}}
							label={this.props.intl.formatMessage({
								id: 'app.signup.last-name-label',
							})}
							disabled={disableForm}
							component={TextField}
							type='text'
							value={props.values.lastName}
							variant='outlined'
							fullWidth={true}
							required={true}
							margin='normal'
							onChange={(e: React.ChangeEvent) => props.handleChange('lastName')(e)}
							error={!!((props.values.lastName.length || props.submitCount) && !!props.errors.lastName)}
							helperText={props.values.lastName.length || props.submitCount ? props.errors.lastName : null}
						/>
						<Field
							name='email'
							label={this.props.intl.formatMessage({
								id: 'app.signup.email-label',
							})}
							disabled={disableForm}
							component={TextField}
							type='email'
							variant='outlined'
							fullWidth={true}
							required={true}
							margin='normal'
							value={props.values.email}
							onChange={(e: React.ChangeEvent) => props.handleChange('email')(e)}
							inputProps={{ 'data-testid': 'email' }}
							error={!!((props.values.email.length || props.submitCount) && !!props.errors.email)}
							helperText={props.values.email.length || props.submitCount ? props.errors.email : null}
						/>
						<Field
							name='password'
							label={this.props.intl.formatMessage({
								id: 'app.signup.password-label',
							})}
							component={PasswordTextFieldComponent}
							disabled={disableForm}
							type='password'
							variant='outlined'
							fullWidth={true}
							required={true}
							margin='normal'
							value={props.values.password}
							onChange={(e: React.ChangeEvent) => props.handleChange('password')(e)}
							inputProps={{
								'data-testid': 'password',
							}}
							error={!!((props.values.password.length || props.submitCount) && !!props.errors.password)}
							helperText={props.values.password.length || props.submitCount ? props.errors.password : null}
						/>
						{!this.isQuickSignUp && !this.state.isSignUpKeyInQueryParam && (
							<FormControlLabel
								control={
									<Checkbox
										checked={this.state.showAPIKeyTextBox}
										onChange={(e) => {
											props.setFieldValue('signupKey', '');
											this.setState((prevState) => ({
												showAPIKeyTextBox: !prevState.showAPIKeyTextBox,
											}));
											this.updateSignupSchema(e.target.checked);
										}}
									/>
								}
								label={<>{intlService.GetFormattedMessage('app.signup.signup-form.signup-key')}</>}
							/>
						)}
						{this.state.showAPIKeyTextBox && (
							<Field
								name='signupKey'
								label={this.props.intl.formatMessage({
									id: 'app.signup.apiKey-label',
								})}
								component={TextField}
								type='text'
								variant='outlined'
								fullWidth={true}
								required={true}
								margin='normal'
								value={props.values.signupKey}
								inputProps={{
									'data-testid': 'signupKey',
								}}
								onChange={(e: React.ChangeEvent) => props.handleChange('signupKey')(e)}
								error={!!((props.values.signupKey.length || props.submitCount) && !!props.errors.signupKey)}
								helperText={props.values.signupKey.length || props.submitCount ? props.errors.signupKey : null}
							/>
						)}
						<Box>
							<FormControlLabel
								control={
									<Checkbox
										checked={this.state.isTermsAccepted || disableForm}
										disabled={disableForm}
										inputProps={{
											'aria-label': 'isTermsAccepted',
										}}
										onChange={() =>
											this.setState((state: models.ISignUpFormComponentState) => ({
												isTermsAccepted: !state.isTermsAccepted,
											}))
										}
									/>
								}
								label={
									<>
										{intlService.GetFormattedMessage('app.signup.signup-form.accept-our-terms-label')}
										<Link href={environment.phTermsAndConditionsUrl} target='_blank'>
											{intlService.GetFormattedMessage('app.signup.signup-form.terms-and-conditions')}
										</Link>
									</>
								}
							/>
						</Box>

						{environment.reCaptchaStatus !== ReCaptchaStatusEnum.Disabled && !disableForm && (
							<React.Fragment>
								<div className='pt-1' />
								<ReCAPTCHA
									sitekey={environment.googleCaptchaKey}
									badge='bottomleft'
									onChange={(token: string) => {
										props.setFieldValue('reCaptchaResponse', token);
									}}
									ref={this.recaptchaRef as React.LegacyRef<ReCAPTCHA> & React.LegacyRef<HTMLDivElement>}
								/>
							</React.Fragment>
						)}
						<div className='pt-1' />
						{/* Button component */}
						{disableForm ? (
							<CustomButton
								fullWidth={true}
								type={ButtonTypeEnum.Button}
								buttonText={intlService.GetFormattedMessage('app.app-view.next-button')}
								onClick={this.props.quickSignUpConfiguration.disabledFormAction}
							/>
						) : (
							<CustomButton
								disabled={
									this.state.loading ||
									(environment.reCaptchaStatus === ReCaptchaStatusEnum.Enabled
										? !props.values.reCaptchaResponse
										: false) ||
									!this.state.isTermsAccepted
								}
								fullWidth={true}
								buttonText={this.getFormSubmitButtonText(this.props.mode === SignUpModeEnum.Quick)}
								loading={this.state.loading}
							/>
						)}
					</Form>
				)}
			/>
		);
	}

	private onUserSignedUp = async (values: models.ISignUpFormFields) => {
		try {
			this.setState({
				loading: true,
			});

			const request: models.IUserSignUpRequest = {
				companyName: values.companyName,
				email: values.email,
				password: values.password,
				firstName: values.firstName,
				lastName: values.lastName,
				signupKey: this.props.signUpKey || values.signupKey,
				reCaptchaResponse: values.reCaptchaResponse,
			};

			const result: models.IUserSignInResponse = await accountService.signUpCustomer(
				request,
				this.props.parentAccountId,
				this.props.partnerAppId,
				this.props.axiosCancellationToken
			);

			// Prepare approve terms and conditions request
			const approveTermsAndConditionsRequest: models.IApproveTermsAndConditions = {
				accountId: result.account.id,
			};

			// Approve terms and condition request
			await accountService.approveTermsAndConditions(approveTermsAndConditionsRequest);

			this.recaptchaRef.current?.reset();
			values.reCaptchaResponse = null;

			// If mode is QUICK then do not set
			// accessToken and refreshToken inside localStorage. Also, do not update Redux store.
			if (this.isQuickSignUp) {
				return this.props.onSignUp(result, { ...values });
			}

			accountService.setAccessToken(result.accessToken, BrowserStorageType.Session);
			accountService.setRefreshToken(result.refreshToken, BrowserStorageType.Session);
			accountService.setUserData(result);
			accountService.VisibleAccountNumber = result.visibleAccountNumber;

			this.setState({ loading: false });

			this.props.onSignUp(result);
		} catch (error) {
			this.setState({ loading: false });

			if (!Axios.isCancel(error)) {
				this.props.onSignUp(null, null, error);
			}
		}
	};

	private updateSignupSchema(check: boolean) {
		switch (check) {
			case true:
				this.signupSchema = Yup.object().shape<models.ISignUpFormFields>({
					...SignupSchemaShape,
					signupKey: Yup.string()
						.trim()
						.required(intlService.GetFormattedMessage('app.signup.signup-form.apikey-validation')),
				});
				break;
			case false:
				this.signupSchema = Yup.object().shape<models.ISignUpFormFields>(SignupSchemaShape);
				break;
		}
	}

	private getFormSubmitButtonText = (quickSignUp: boolean) => {
		return intlService.GetFormattedMessage(quickSignUp ? 'app.app-view.next-button' : 'app.signup.signup-button-text');
	};

	private updateSignUpFormData = () => {
		if (this.props.quickSignUpConfiguration?.prefillFormValues) {
			this.FormFactory = { ...this.props.quickSignUpConfiguration?.prefillFormValues };
		}
	};
}

export default withSnackBar(injectIntl(SignUpFormComponent));



// WEBPACK FOOTER //
// ./src/components/account/signup/sign-up-form.component.tsx